/**
 * **校准余额的纯判据**（`DR-056` · 批次 96）
 *
 * ## 为什么要单独一个文件（而不是都写在对话框里）
 *
 * 端侧测试基座是 `react-dom/server`（零新依赖，见 `test/render.tsx`），而
 * **MUI `Dialog` 的内容挂在 Portal 上、SSR 不输出** ⇒ 对话框内部的一切断言都看不到。
 * 把"默认填什么 / 差额多少 / 能不能提交"这三个判据抽出来，就能用纯函数钉住，
 * 组件只剩下"把值摆到屏幕上"。
 *
 * 🔴 这三条不是"为了测试而抽"的：它们各自对应一个**真实会犯的错**：
 *
 * | 判据 | 犯错的后果 |
 * |---|---|
 * | 默认值 = 当前余额 | 默认 0 ⇒ 用户每填一次就"归零"，少敲一个 0 就是 10 倍误差 |
 * | 差额 = 目标 − 当前 | 反号 ⇒ 补的变成扣的（余额差两倍差额） |
 * | 零差额不可提交 | 服务端 400，端侧却把注定失败的请求发出去 |
 *
 * ## ⚠️ 金额解析**不在这里实现**
 *
 * 一律用 `features/txn-draft.ts` 的 `yuanTextToCentsOrNull()` —— 它已经把
 * 「最多两位小数 + 十进制精确（不经浮点）+ 非法返回 `null`」这一整套判据写死了，
 * 记一笔 / 还款 / 校准三处共用同一份（否则记一笔允许 `0.07`、校准允许 `0.070`
 * 这类口径分裂必然出现）。
 */

import { centsToYuanText, toCents } from '@jiaying/shared';
import { yuanTextToCentsOrNull } from './txn-draft.js';

/**
 * 目标余额输入框的**默认文本** = 当前余额（定点两位小数）。
 *
 * 预填而不是留空：用户只需要改那几个不对的数字，不必从 0 开始敲。
 */
export function defaultAdjustText(currentBalance: number): string {
  return centsToYuanText(toCents(currentBalance));
}

/**
 * 差额 = 目标 − 当前（整数分）；目标格式非法 ⇒ `null`。
 *
 * **正 = 余额变多**（补一笔调整），负 = 余额变少。
 */
export function adjustDeltaOf(targetText: string, currentBalance: number): number | null {
  const target = yuanTextToCentsOrNull(targetText);
  if (target === null) return null;
  return (target as number) - currentBalance;
}

/**
 * 输入格式问题（`null` = 没有）。
 *
 * ⚠️ 只判"能不能解析成整数分"，**不判上界** —— 上界由服务端契约的 `CentsSchema` 兜
 * （端侧再写一遍阈值就是第二个真值来源）。
 */
export function adjustIssueOf(targetText: string, currentBalance: number): string | null {
  if (targetText.trim() === '') return '请输入实际余额';
  return adjustDeltaOf(targetText, currentBalance) === null ? '最多两位小数' : null;
}

/**
 * 能否提交：格式合法 **且** 差额非零 **且** 不在提交中。
 *
 * 🔴 零差额必须挡住：服务端会 400（`DR-056` §实施清单），
 * 端侧把注定失败的请求发出去只是让用户多等一个来回。
 */
export function canSubmitAdjust(
  targetText: string,
  currentBalance: number,
  busy: boolean,
): boolean {
  if (busy) return false;
  if (adjustIssueOf(targetText, currentBalance) !== null) return false;
  return adjustDeltaOf(targetText, currentBalance) !== 0;
}
